// utils/auth.js
// utils/globalAuth.js

const STORE_KEY = "userSession";
const CLUB_KEY = "clubId";

/**
 * 권한 저장 (객체를 문자열로 변환하여 저장)
 * @param data
 */
export const setSessions = (data) => {
  sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
};

/**
 * 권한 가져오기 (없으면 빈 객체 반환)
 * @returns {Object}
 */
export const getSessions = () => {
  const clubId = sessionStorage.getItem(CLUB_KEY);
  const stored = sessionStorage.getItem(STORE_KEY);

  try {
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    console.warn("Failed to parse permissions:", e);
    return {};
  }
};

/**
 * Web Push VAPID Public Key 반환
 * @returns {string|null}
 */
export const getWebPushPublicKey = () => {
  const stored = sessionStorage.getItem(STORE_KEY);

  try {
    const session = stored ? JSON.parse(stored) : null;
    return session?.webPushKey || null;
  } catch (e) {
    console.warn("Failed to parse web push key:", e);
    return null;
  }
};

/**
 * 골프장 사업주 여부 반환
 * @returns Boolean
 */
export const isBusinessOwner = () => {
  const stored = sessionStorage.getItem(STORE_KEY);

  try {
    return stored ? JSON.parse(stored)["isBusinessOwner"] : false;
  } catch (e) {
    console.warn("Failed to parse permissions:", e);
    return {};
  }
};

/**
 * 골프장 관리자 여부 반환
 * @returns Boolean
 */
export const isAdminOwner = () => {
  const stored = sessionStorage.getItem(STORE_KEY);

  try {
    return stored ? JSON.parse(stored)["isAdmin"] : false;
  } catch (e) {
    console.warn("Failed to parse permissions:", e);
    return {};
  }
};

/**
 * 유저 권한 반환
 * @returns {Object}
 */
export const getPermissions = () => {
  const stored = sessionStorage.getItem(STORE_KEY);

  try {
    return stored ? JSON.parse(stored)["userPermissions"] : {};
  } catch (e) {
    console.warn("Failed to parse permissions:", e);
    return {};
  }
};

/**
 * 유저 골프장 권한 반환
 *
 * 권한 룰 종류 정의 (Permission Rules)
 *
 * 각 권한 항목은 다음과 같은 CRUD 형태로 구성됩니다:
 *  - isRead   : 읽기 권한
 *  - isCreate : 생성 권한
 *  - isUpdate : 수정 권한
 *  - isDelete : 삭제 권한
 *
 * 사용 가능한 권한 항목 (리소스 종류):
 *  - ACCOUNT           : 계정 관리
 *  - CLUB_INFORMATION  : 골프장 정보
 *  - DASHBOARD         : 대시보드 접근
 *  - MEMBER            : 회원 관리
 *  - RESERVATION       : 예약 관리
 *  - ROLE_SETTING      : 역할/권한 설정
 *  - SCHEDULE          : 스케줄 관리
 *  - STAFF             : 직원 관리
 *
 * @returns {Object}
 */
export const getPermissionsByClub = (role, targetClubId) => {
  const clubId = targetClubId ?? sessionStorage.getItem(CLUB_KEY);
  const AllPermissions = getPermissions();
  let permissions = AllPermissions?.[clubId] || {};

  if (role) {
    permissions = permissions[role];
  }

  return permissions;
};

/**
 * 하나라도 만족하는 권한 있는지 반환
 *
 * 권한 룰 종류 정의 (Permission Rules)
 *
 * 각 권한 항목은 다음과 같은 CRUD 형태로 구성됩니다:
 *  - isRead   : 읽기 권한
 *  - isCreate : 생성 권한
 *  - isUpdate : 수정 권한
 *  - isDelete : 삭제 권한
 *
 * 사용 가능한 권한 항목 (리소스 종류):
 *  - ACCOUNT           : 계정 관리
 *  - CLUB_INFORMATION  : 골프장 정보
 *  - DASHBOARD         : 대시보드 접근
 *  - MEMBER            : 회원 관리
 *  - RESERVATION       : 예약 관리
 *  - ROLE_SETTING      : 역할/권한 설정
 *  - SCHEDULE          : 스케줄 관리
 *  - STAFF             : 직원 관리
 *
 * @returns Boolean
 */
export const hasPermissions = (role, perm) => {
  const permissions = getPermissions();

  return Object.values(permissions).some(
    (section) => section?.[role]?.[perm] === true
  );
};

/**
 * 권한 갱신
 * @param {Object} newPermissions 갱신할 권한 데이터 (기존 권한을 덮어쓸 수 있음)
 */
export const updatePermissions = (newPermissions) => {
  const currentSessions = getSessions(); // 현재 세션 데이터를 가져옵니다.

  // 기존 세션에서 'userPermissions' 데이터를 추출하고 새로운 권한으로 갱신합니다.
  const updatedSessions = {
    ...currentSessions,
    userPermissions: {
      ...currentSessions.userPermissions,
      ...newPermissions, // 새로운 권한을 덮어씌웁니다.
    },
  };

  // 갱신된 세션 데이터를 다시 저장합니다.
  setSessions(updatedSessions);
};

/**
 * 권한 삭제
 */
export const clearSessions = () => {
  sessionStorage.removeItem(STORE_KEY);
};
